import React from 'react';
import { notFound } from 'next/navigation';
import { ArticleJsonLd } from 'next-seo';
import { Box, Divider, Heading } from '@chakra-ui/react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ChakraUIRenderer from './ChakraUIRenderer';
import BlogHeader from './BlogHeader';
// import MoreStories from './MoreStories';
import PostHeader from './PostHeader';
import type { Post, Settings } from '../../libs/sanity.queries';
import Page from '../Page';
import { urlForImage } from '../../libs/sanity.image';
import AlertBanner from './AlertBanner';
import Avatar from './AuthorAvatar';
import MoreStories from './MoreStories';

export interface PostPageProps {
  preview?: boolean;
  loading?: boolean;
  post: Post;
  morePosts: Post[];
  settings: Settings;
}

const NO_POSTS: Post[] = [];

const PostPage = (props: PostPageProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { preview, loading, morePosts = NO_POSTS, post, settings } = props;
  const { title = post.title } = settings || {};
  const slug = post?.slug;

  if (!slug && !preview) {
    notFound();
  }

  return (
    <Page
      seoOptions={{
        title: post.title || 'SmartParse',
        description: post.description,
        canonical: `https://smartparse.io/posts/${post.slug}/`,
        openGraph: {
          title: post.title,
          description: post.description || '',
          url: `https://smartparse.io/posts/${post.slug}/`,
          type: 'article',
          article: {
            publishedTime: post.date,
            // eslint-disable-next-line no-underscore-dangle
            modifiedTime: post._updatedAt,
            // section: 'Section II',
            authors: [
              // 'https://www.example.com/authors/@firstnameA-lastnameA', TODO: Add author social media links
              // 'https://www.example.com/authors/@firstnameB-lastnameB'
            ]
            // tags: ['Tag A', 'Tag B', 'Tag C']
          },
          images: [
            {
              // eslint-disable-next-line no-underscore-dangle
              url: post.mainImage?.asset?._ref && [
                urlForImage(post.mainImage).width(1200).height(627).fit('crop').url()
              ],
              width: 1200,
              height: 627,
              alt: post.title // TODO: Image alt
            }
          ]
        }
      }}
      maxW='100%'
      p={0}
      m={0}
    >
      <ArticleJsonLd
        type='BlogPosting'
        url={`https://smartparse.io/posts/${post.slug}/`}
        title={post.title}
        images={[post.mainImage && urlForImage(post.mainImage).width(1200).height(627).fit('crop').url()]}
        datePublished={post.date}
        // eslint-disable-next-line no-underscore-dangle
        dateModified={post._updatedAt}
        authorName={post.author.name}
        description={post.description}
      />

      <Box maxWidth='5xl' mx='auto' px={10} pb={20}>
        <AlertBanner preview={preview} loading={loading} />

        <BlogHeader title={title} level={2} />

        {preview && !post ? (
          <Heading
            as='h1'
            mb={12}
            textAlign={{ base: 'center', md: 'left' }}
            size={{ base: '6xl', md: '7xl', lg: '8xl' }}
            fontWeight='extrabold'
            className='leading-tight tracking-tighter md:leading-none lg:text-8xl'
          >
            Loading…
          </Heading>
        ) : (
          <>
            <article data-content='main'>
              <PostHeader title={post.title} coverImage={post.mainImage} date={post.date} author={post.author} />
              <Markdown remarkPlugins={[remarkGfm]} components={ChakraUIRenderer()} skipHtml>
                {typeof post.body === 'string' ? post.body : ''}
              </Markdown>
            </article>

            <Box mt={20} mb={10}>
              <Avatar name={post.author.name} jobTitle={post.author.jobTitle} image={post.author.image} />
            </Box>

            <Divider orientation='horizontal' py={2} borderColor='bg-darker' />

            {morePosts?.length > 0 && <MoreStories posts={morePosts} />}
          </>
        )}
      </Box>
    </Page>
  );
};

export default PostPage;
